export interface CountryCallingCode {
  countryCode: string;
  callingCode: string;
  name: string;
  flag: string;
  nationalNumberLength: number; // typical national significant number length
  pattern?: number[]; // grouping of digits, e.g. [3, 3, 2, 2] for RU (963 383 29 47)
  example: string;
}

export const SUPPORTED_COUNTRY_CALLING_CODES: CountryCallingCode[] = [
  {
    countryCode: 'FR',
    callingCode: '+33',
    name: 'France',
    flag: '🇫🇷',
    nationalNumberLength: 9,
    pattern: [1, 2, 2, 2, 2],
    example: '1 23 45 67 89',
  },
  {
    countryCode: 'ES',
    callingCode: '+34',
    name: 'Espagne',
    flag: '🇪🇸',
    nationalNumberLength: 9,
    pattern: [3, 3, 3],
    example: '123 456 789',
  },
  {
    countryCode: 'MX',
    callingCode: '+52',
    name: 'Mexique',
    flag: '🇲🇽',
    nationalNumberLength: 10,
    pattern: [2, 4, 4],
    example: '01 2345 6789',
  },
  {
    countryCode: 'DE',
    callingCode: '+49',
    name: 'Allemagne',
    flag: '🇩🇪',
    nationalNumberLength: 10,
    pattern: [3, 3, 4],
    example: '012 345 6789',
  },
  {
    countryCode: 'GB',
    callingCode: '+44',
    name: 'Royaume-Uni',
    flag: '🇬🇧',
    nationalNumberLength: 10,
    pattern: [4, 6],
    example: '0123 456789',
  },
  {
    countryCode: 'US',
    callingCode: '+1',
    name: 'États-Unis / Canada',
    flag: '🇺🇸',
    nationalNumberLength: 10,
    pattern: [3, 3, 4],
    example: '012 345 6789',
  },
  {
    countryCode: 'IT',
    callingCode: '+39',
    name: 'Italie',
    flag: '🇮🇹',
    nationalNumberLength: 10,
    pattern: [3, 3, 4],
    example: '012 345 6789',
  },
  {
    countryCode: 'PT',
    callingCode: '+351',
    name: 'Portugal',
    flag: '🇵🇹',
    nationalNumberLength: 9,
    pattern: [3, 3, 3],
    example: '123 456 789',
  },
  {
    countryCode: 'BE',
    callingCode: '+32',
    name: 'Belgique',
    flag: '🇧🇪',
    nationalNumberLength: 9,
    pattern: [3, 2, 2, 2],
    example: '123 45 67 89',
  },
  {
    countryCode: 'CH',
    callingCode: '+41',
    name: 'Suisse',
    flag: '🇨🇭',
    nationalNumberLength: 9,
    pattern: [2, 3, 2, 2],
    example: '12 345 67 89',
  },
  {
    countryCode: 'RU',
    callingCode: '+7',
    name: 'Russie',
    flag: '🇷🇺',
    nationalNumberLength: 10,
    pattern: [3, 3, 2, 2],
    example: '012 345 67 89',
  },
  {
    countryCode: 'MA',
    callingCode: '+212',
    name: 'Maroc',
    flag: '🇲🇦',
    nationalNumberLength: 9,
    pattern: [3, 3, 3],
    example: '123 456 789',
  },
  {
    countryCode: 'DZ',
    callingCode: '+213',
    name: 'Algérie',
    flag: '🇩🇿',
    nationalNumberLength: 9,
    pattern: [3, 2, 2, 2],
    example: '123 45 67 89',
  },
  {
    countryCode: 'TN',
    callingCode: '+216',
    name: 'Tunisie',
    flag: '🇹🇳',
    nationalNumberLength: 8,
    pattern: [2, 3, 3],
    example: '12 345 678',
  },
  {
    countryCode: 'SN',
    callingCode: '+221',
    name: 'Sénégal',
    flag: '🇸🇳',
    nationalNumberLength: 9,
    pattern: [2, 3, 2, 2],
    example: '12 345 67 89',
  },
  {
    countryCode: 'CI',
    callingCode: '+225',
    name: 'Côte d’Ivoire',
    flag: '🇨🇮',
    nationalNumberLength: 10,
    pattern: [2, 2, 2, 2, 2],
    example: '01 23 45 67 89',
  },
];

export const DEFAULT_CALLING_CODE = SUPPORTED_COUNTRY_CALLING_CODES[0]; // France +33

/**
 * Returns the matching country calling code object from a locale code (e.g. 'es-MX' -> +52, 'es-ES' -> +34, 'fr-FR' -> +33)
 */
export function getCallingCodeForLocale(locale?: string | null): CountryCallingCode {
  if (!locale) return DEFAULT_CALLING_CODE;
  const normalized = locale.toUpperCase();
  if (normalized.includes('MX'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'MX') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('ES'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'ES') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('DE'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'DE') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('GB') || normalized.includes('EN-GB'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'GB') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('US') || normalized.includes('EN'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'US') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('IT'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'IT') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('PT'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'PT') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('BE'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'BE') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('CH'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'CH') ?? DEFAULT_CALLING_CODE
    );
  if (normalized.includes('RU'))
    return (
      SUPPORTED_COUNTRY_CALLING_CODES.find(c => c.countryCode === 'RU') ?? DEFAULT_CALLING_CODE
    );
  return DEFAULT_CALLING_CODE;
}
